import DialogContent from "@mui/material/DialogContent";
import { BulkActionType, IBulkEventPayload, IBulkOpsState, IFlight, IGroundEvent } from "@vmosolutions/oc-interfaces";
import React, { useCallback, useEffect, useMemo, useState } from "react";

import { bulkEvent } from "src/redux/actions/flights/FlightData.Actions";
import { IRootState } from "src/redux/reducers/CombinedReducers";
import { LoadingMessage } from "../../LoadingMessage/LoadingMessage";

import { Button, DialogActions, Grid, useTheme } from "@mui/material"; //Typography
import Slider from "@mui/material/Slider";
import { useDispatch, useSelector } from "react-redux";
import { EventModalTypes } from "src/components/DialogModal/DialogModalWrapper";
import { FlightChipCollection } from "src/components/Flights/FlightChip";
import { DateTimeField } from "src/components/FormikComponents/DateTimeField";
import { DurationField } from "src/components/FormikComponents/DurationField";
import { SwitchField } from "src/components/FormikComponents/SwitchField";
import { setSelectedEvents } from "src/redux/actions/flights/FlightTimeline.Actions";
import { isFlight } from "src/redux/reducers/FlightTimelineReducer";
import { updateAirportOptions } from "src/utils/airportHelper"; //findAirports, filterAirports
import { milli2DaysHrsMin } from "src/utils/index";
import { ModalButton, ModalErrorText, ModalMessage, ModalTitle } from "../../DialogModal/DialogModalComponents";
import { DialogModalStyles } from "../DialogModalWrapper";

const milsecondsInMinute = 60000;
// The number of days we allow +/- from the first event in the date picker
const maxDelayDays = 3;
/**
 * @param isOpen - to keep track of whither the modal should be open or closed
 * @param handleClose - sets isOpen to true or false in DialogModalWrapper.tsx
 * @param setActiveFlightModal - sets the  state of activeFlightModal in FlightTimelineWrapper.tsx
 * @returns the modal
 */

interface IDelayEventModalProps {
  isOpen: boolean;
  handleClose: (value: boolean) => void;
  setActiveEventModal: (EventModalTypes: EventModalTypes) => void;
}

export const DelayEventModal = (props: IDelayEventModalProps): JSX.Element => {
  const dispatch = useDispatch();
  const theme = useTheme();
  const { isOpen, handleClose, setActiveEventModal } = props;
  const selectedEvents: (IFlight | IGroundEvent)[] = useSelector(
    (state: IRootState) => state.timelineFlights.selectedEvents
  );

  const airportOptions = useSelector((state: IRootState) => state.structures.airportOptions);

  // For BULK batch tracking
  const [batchId, setBatchId] = useState<any>(null);
  const batchJob: IBulkOpsState = useSelector((state: IRootState) => state.bulkBatch[batchId]);
  const [building, setBuilding] = useState<boolean>(false);
  const [buildStatus, setBuildStatus] = useState<string>(null);

  const [delayDuration, setDelayDuration] = useState<number>(0);
  const [timeError, setTimeError] = useState<string>(null);
  const [receivedMessagesError, setReceivedMessageError] = useState<string>(null);

  const selectedFlights: IFlight[] = React.useMemo(() => {
    return selectedEvents.filter((event: IFlight | IGroundEvent) => isFlight(event));
  }, [selectedEvents]);

  const selectedGroundEvents: IGroundEvent[] = React.useMemo(() => {
    return selectedEvents.filter((event: IFlight | IGroundEvent) => !isFlight(event)) as IGroundEvent[];
  }, [selectedEvents]);

  // get type of event
  const typeFlight: boolean = selectedFlights.length > 0 ? true : false;
  const firstEvent = useMemo(() => {
    //let earliestEventStart: number;
    //let earliestEventEnd: number;
    let messagesReceived = false;
    if (selectedFlights.length > 0) {
      selectedFlights.forEach((flight) => {
        //console.log("flight", flight);
        if (flight.receivedDepartureMessage || flight.receivedArrivalMessage) {
          messagesReceived = true;
        }
      });
      if (messagesReceived) {
        setReceivedMessageError(
          "Cannot delay flight(s) that have already departed. Please use the edit flight window for these flights or re-select a group of flights that have not departed."
        );
      }
      return selectedFlights.sort((a, b) => a.actualOff?.valueOf() - b.actualOff?.valueOf())[0];
    }
    if (selectedGroundEvents.length > 0) {
      return selectedGroundEvents.sort((a, b) => a.actualStart?.valueOf() - b.actualStart?.valueOf())[0];
    }
    return null;
  }, [selectedFlights, selectedGroundEvents]);

  const [newStart, setNewStart] = useState<number>(undefined);
  const [newEnd, setNewEnd] = useState<number>(undefined);

  const loadAirportData = useCallback(
    (icao: string) => {
      updateAirportOptions(icao, airportOptions, dispatch);
    },
    [airportOptions, dispatch]
  );

  useEffect(() => {
    if (!firstEvent) {
      return;
    }
    // setFirstEventTime(firstEvent);
    if (isFlight(firstEvent)) {
      loadAirportData(firstEvent.origin);
      loadAirportData(firstEvent.destination);
    } else if (!isFlight(firstEvent)) {
      loadAirportData(firstEvent.location);
    }
  }, [firstEvent, loadAirportData]);

  const delayTimeInMilseconds = delayDuration * milsecondsInMinute;

  useEffect(() => {
    if (!firstEvent) {
      return;
    }
    const defaultStart = isFlight(firstEvent) ? firstEvent.actualOut?.valueOf() : firstEvent.actualStart?.valueOf();
    const defaultEnd = isFlight(firstEvent) ? firstEvent.actualIn?.valueOf() : firstEvent.actualEnd?.valueOf();
    setNewStart(defaultStart + delayTimeInMilseconds);
    setNewEnd(defaultEnd + delayTimeInMilseconds);
  }, [firstEvent, delayTimeInMilseconds]);

  // Find delta (in minutes) and apply to delay;
  const handleChangeStart = useCallback(
    (updatedStart: number) => {
      const defaultStart = isFlight(firstEvent) ? firstEvent.actualOut?.valueOf() : firstEvent?.actualStart?.valueOf();
      setDelayDuration(Math.floor((updatedStart - defaultStart) / 60000));
    },
    [firstEvent]
  );

  // Find delta (in minutes) and apply to delay;
  const handleChangeEnd = useCallback(
    (updatedEnd: number) => {
      const defaultEnd = isFlight(firstEvent) ? firstEvent.actualIn?.valueOf() : firstEvent?.actualEnd?.valueOf();
      setDelayDuration(Math.floor((updatedEnd - defaultEnd) / 60000));
    },
    [firstEvent]
  );

  const groundEventPluralFormat = selectedGroundEvents.length > 1 ? "Ground Events" : "Ground Event";
  const flightPluralFormat = selectedFlights.length > 1 ? "Flights" : "Flight";

  useEffect(() => {
    if (batchJob) {
      //console.log(batchJob);
      const eventTypes = typeFlight ? "flights" : "ground events";
      const type = delayDuration > 0 ? "delayed" : "expedited";
      const { records, successfulEventIds, failedEvents } = batchJob;
      const failedUpdates = failedEvents?.length > 0 ? `, ${failedEvents?.length} ${eventTypes} failed update` : "";
      setBuildStatus(`${successfulEventIds?.length} of ${records} ${eventTypes} successfully ${type}${failedUpdates}`);

      // Build completed
      if (successfulEventIds?.length + failedEvents?.length === records) {
        setBuilding(false);
      }
      if (successfulEventIds?.length === records) {
        handleClose(false);
        setActiveEventModal(EventModalTypes.None);
      }
    }
  }, [batchJob, typeFlight, delayDuration, handleClose, setActiveEventModal]);

  //check to make sure you aren't working with an empty array
  if (selectedEvents.length <= 0) {
    // Debug.log("selectedEvents array is empty")
    return null;
  }

  ///////////////////////////////
  // String Formatting Helpers //
  ///////////////////////////////

  //to format the delay message of the current slider's selection
  const formatDelayTime = (delayTime: number): string => {
    if (delayTime === 0) {
      return "0 minutes";
    }

    const direction: string = delayTime < 0 ? "-" : "";
    const time = Math.abs(delayTime);

    const format = (timeValue: number, timeUnit: string): string => {
      if (timeValue === 0) {
        return "";
      }
      switch (timeUnit) {
        case "minute":
          return timeValue > 1 ? `${timeValue} minutes` : `${timeValue} minute`;
        case "hour":
          return timeValue > 1 ? `${timeValue} hours` : `${timeValue} hour`;
        default:
          return "";
      }
    };
    return `${direction} ${format(Math.trunc(time / 60), "hour")} ${format(time % 60, "minute")}`;
  };

  // This takes a reference date and adds a number of days. If the reference date is falsy it returns undefined.
  const dateWindow = (refDate: Date | number | undefined | null, days: number): number | undefined => {
    if (!refDate) return undefined;
    const rd = new Date(refDate?.valueOf());
    const startOfDate = new Date(rd.getFullYear(), rd.getMonth(), rd.getDate());
    return startOfDate?.valueOf() + days * 60 * 24 * milsecondsInMinute;
  };

  const marks = [
    { value: 0, label: "0" },
    //{ value: 60, },
    { value: 120, label: "2 " },
    //{ value: 180, label : "" },
    { value: 240, label: "4 " },
    //{ value: 300, label : ""},
    { value: 360, label: "6 " },
    //{ value: 420, label : "" },
    { value: 480, label: "8 " },
    //{ value: 540, label : ""},
    { value: 600, label: "10" },
    { value: 720, label: "12" },
    //{ value: -60, },
    { value: -120, label: "-2 " },
    //{ value: -180, label : "" },
    { value: -240, label: "-4 " },
    //{ value: -300, label : ""},
    { value: -360, label: "-6 " },
    //{ value: -420, label : "" },
    { value: -480, label: "-8 " },
    //{ value: -540, label : ""},
    { value: -600, label: "-10" },
    { value: -720, label: "-12" },
  ];

  //////////////
  // HANDLERS //
  //////////////

  const getTitle = (): string => {
    if (typeFlight) {
      return `Delay/Expedite ${flightPluralFormat}`;
    } else {
      return `Delay/Expedite ${groundEventPluralFormat}`;
    }
  };

  const handleDurationChange = (milliseconds: number): void => {
    setDelayDuration(milliseconds / 60000); // convert milliseconds to minutes
    setTimeError("");
  };

  const handleSliderChange = (event: any, sliderValue: any): void => {
    setDelayDuration(sliderValue);
    setTimeError("");
  };

  const handleCancel = (): void => {
    // If a bulk operation was started and users choose to close the modal, clear all selected events making it easier for them to start a new bulk operation.
    if (buildStatus) dispatch(setSelectedEvents([]));
    handleClose(false);
    setActiveEventModal(EventModalTypes.None);
  };

  const handleConfirm = async (): Promise<void> => {
    //if there is no change do not update DB
    if (delayDuration === 0) {
      //give message that the time change is zero
      setTimeError("Select a delay greater or less than 0 minutes");
      return;
    }
    const delayTimeInMilseconds = delayDuration * milsecondsInMinute;

    if (typeFlight) {
      setBuilding(true);
      setBuildStatus("Changing actual Off/On times for selected flights");
      const selectedEventIds: string[] = [];
      const selectedFlightChanges = selectedFlights.map((flight: IFlight) => {
        selectedEventIds.push(flight._id);
        return {
          _id: flight._id,
          actualOff: flight.actualOff?.valueOf() + delayTimeInMilseconds,
          actualOn: flight.actualOn?.valueOf() + delayTimeInMilseconds,
          actualIn: flight.actualIn?.valueOf() + delayTimeInMilseconds,
          actualOut: flight.actualOut?.valueOf() + delayTimeInMilseconds,
        };
      });
      const bulkPayload: IBulkEventPayload = {
        actionType: BulkActionType.UPDATE_FLIGHT,
        batchIds: selectedEventIds,
        payload: selectedFlightChanges,
      };
      const newBatchId = await dispatch(bulkEvent(bulkPayload, selectedFlights?.length));
      setBatchId(newBatchId);
    } else {
      setBuilding(true);
      setBuildStatus("Changing Start/End times for selected ground events");
      const selectedEventIds: string[] = [];
      const selectedEventChanges = selectedGroundEvents.map((event: IGroundEvent) => {
        selectedEventIds.push(event._id);
        return {
          _id: event._id,
          actualStart: event.actualStart?.valueOf() + delayTimeInMilseconds,
          actualEnd: event.actualEnd?.valueOf() + delayTimeInMilseconds,
        };
      });
      const bulkPayload: IBulkEventPayload = {
        actionType: BulkActionType.UPDATE_GROUND_EVENT,
        batchIds: selectedEventIds,
        payload: selectedEventChanges,
      };
      const newBatchId = await dispatch(bulkEvent(bulkPayload, selectedGroundEvents?.length));
      setBatchId(newBatchId);
    }
    setActiveEventModal(EventModalTypes.None);
    handleClose(false);
  };

  if (isOpen) {
    return (
      <DialogContent sx={DialogModalStyles(theme)}>
        <div className="modal_content" style={{ minWidth: 400, padding: 20 }}>
          {!!typeFlight && <FlightChipCollection flights={selectedFlights} disableContext={true} disableFocus={true} />}
          <ModalTitle title={getTitle()}></ModalTitle>

          <Grid container direction="column" style={{ width: 400 }}>
            <Grid container>
              <Grid item style={{ marginLeft: 15 }}>
                <DateTimeField
                  className={`auto_width datefield ${delayDuration !== 0 && "dirty"}`}
                  label={`First ${typeFlight ? "Flight Departure" : "Event Start"}`}
                  name={"delay_expedite_start"}
                  value={newStart}
                  onChange={(value) => handleChangeStart(value as number)}
                  isEpoch={true}
                  minDate={dateWindow((firstEvent as IFlight).scheduledOut, -1 * maxDelayDays)}
                  maxDate={dateWindow((firstEvent as IFlight).scheduledIn, maxDelayDays)}
                  showAirportTime={typeFlight ? (firstEvent as IFlight).origin : (firstEvent as IGroundEvent).location}
                />
              </Grid>
              <Grid item style={{ marginLeft: 15 }}>
                <DateTimeField
                  className={`auto_width datefield ${delayDuration !== 0 && "dirty"}`}
                  label={`First ${typeFlight ? "Flight Arrival" : "Event End"}`}
                  name={"delay_expedite_end"}
                  value={newEnd}
                  onChange={(value) => handleChangeEnd(value as number)}
                  isEpoch={true}
                  minDate={dateWindow((firstEvent as IFlight).scheduledOut, -1 * maxDelayDays)}
                  maxDate={dateWindow((firstEvent as IFlight).scheduledIn, maxDelayDays)}
                  showAirportTime={
                    typeFlight ? (firstEvent as IFlight).destination : (firstEvent as IGroundEvent).location
                  }
                />
              </Grid>
            </Grid>
            <Grid container style={{ width: 370 }} wrap="nowrap">
              <Grid item>
                <SwitchField
                  className={`delay_mode_selector ${delayDuration <= 0 && "active"}`}
                  name={"delay_mode_selector"}
                  value={delayDuration === 0 ? null : delayDuration <= 0}
                  onChange={() => {
                    if (delayDuration <= 0) {
                      setDelayDuration(Math.abs(delayDuration));
                    } else {
                      setDelayDuration(-delayDuration);
                    }
                  }}
                  switchlabels={["Expedite", "Delay"]}
                  switchtooltips={["", ""]}
                  label=""
                  allowNull={true}
                  inlineSx={{
                    "& button": { height: "20px !important" },
                    "& .button2": {
                      marginRight: "15px",
                    },
                  }}
                  // disabled={!formProps?.values?.locationICAO}
                />
              </Grid>
              <DurationField
                className={`textfield ${delayDuration !== 0 && "dirty"}`}
                value={delayTimeInMilseconds}
                onChange={(value: any) => {
                  handleDurationChange(value as number);
                }}
                placeholder="example: 2h 30m"
              />
            </Grid>
          </Grid>

          <div className="slider-container">
            <Slider
              id="Delay Slider"
              className={"slider"}
              valueLabelDisplay="off"
              aria-label="modal slider"
              // defaultValue={0} shouldn't use defaultValue is value is passed.
              marks={marks}
              step={5}
              min={-720}
              max={720}
              value={delayDuration ?? 0}
              onChange={handleSliderChange}
              disabled={building || !!buildStatus}
            />
          </div>

          <Grid container alignItems="center" style={{ padding: "0px 15px" }}>
            <Grid container alignItems="center">
              <Button
                style={{ margin: 5, marginBottom: 15, borderRadius: 2, height: 26, fontSize: 10, padding: "0px 8px" }}
                variant="outlined"
                color="secondary"
                disabled={delayDuration === 0}
                onClick={() => setDelayDuration(0)}
              >
                Reset
              </Button>
              <ModalMessage
                message={
                  delayDuration === 0
                    ? "No Change"
                    : delayDuration > 0
                      ? "Delay by " + milli2DaysHrsMin(Math.abs(delayTimeInMilseconds)) //formatDelayTime(Math.abs(delayDuration))
                      : "Expedite by " + milli2DaysHrsMin(Math.abs(delayTimeInMilseconds)) //formatDelayTime(Math.abs(delayDuration))
                }
              />
            </Grid>
          </Grid>

          {!!timeError && <ModalErrorText error={timeError}></ModalErrorText>}
          {!!receivedMessagesError && <ModalErrorText error={receivedMessagesError}></ModalErrorText>}

          <DialogActions>
            <ModalButton label={buildStatus ? "Close" : "Cancel"} handler={handleCancel}></ModalButton>
            <ModalButton
              label="Confirm"
              disable={!!timeError || !!receivedMessagesError || delayDuration === 0 || building || !!buildStatus}
              handler={handleConfirm}
            ></ModalButton>
          </DialogActions>

          <div className="modal_status">
            {!!buildStatus && <LoadingMessage message={buildStatus} animate={building} />}
          </div>
        </div>
      </DialogContent>
    );
  } else {
    return null;
  }
};
